import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import StarRating from '../../../components/ui/StarRating';

const ListingCard = ({ listing, onSave, isAuthenticated, showViewCount = false }) => {
  const [isSaved, setIsSaved] = useState(listing?.isFavorited || false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Update saved state when prop changes (for favorites sync)
  useEffect(() => {
    setIsSaved(listing?.isFavorited || false);
  }, [listing?.isFavorited]);

  const handleSave = async (e) => {
    e?.stopPropagation();
    
    if (!isAuthenticated) {
      onSave?.(listing?.id, 'login-required');
      return;
    }

    setIsLoading(true);
    try {
      await onSave?.(listing?.id, !isSaved);
      setIsSaved(!isSaved);
    } catch (error) {
      console.error('Error saving listing:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = async () => {
    try {
      console.log('ListingCard: Navigating to business detail with ID:', listing?.id);
      
      // Navigate to business detail page using the business ID
      navigate(`/business-detail?id=${listing?.id}`);
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback navigation in case of errors
      navigate('/business-detail', { state: { listingId: listing?.id } });
    }
  };

  const formatDistance = (distance) => {
    if (!distance) return null;
    if (distance < 1) return `${Math.round(distance * 1000)}m away`;
    return `${distance?.toFixed(1)}km away`;
  };

  // Enhanced image fallback logic with proper error handling
  const getMainImage = () => {
    return listing?.image || listing?.cover_image_url || listing?.logo_url || null;
  };

  // Format business location with fallback
  const formatLocation = () => {
    if (listing?.location && listing?.location !== 'Location not specified') {
      return listing?.location;
    }
    
    const locationParts = [listing?.city, listing?.county, listing?.postcode]?.filter(Boolean);
    return locationParts?.length > 0 ? locationParts?.join(', ') : 'Location not specified';
  };

  // CRITICAL FIX: Ensure categories is always an array
  const safeCategories = Array.isArray(listing?.categories) ? listing?.categories : 
                         (listing?.categories ? [listing?.categories] : []);

  return (
    <div 
      className="bg-card rounded-lg shadow-organic hover:shadow-organic-md transition-organic cursor-pointer group"
      onClick={handleCardClick}
    >
      {/* Enhanced Image Container with Better Fallback */}
      <div className="relative overflow-hidden rounded-t-lg h-48 bg-muted">
        <Image
          src={getMainImage()}
          alt={listing?.name || 'Business image'}
          className="w-full h-full object-cover group-hover:scale-105 transition-organic"
        />
        
        {/* Save Button */}
        {onSave && (
          <button
            onClick={handleSave}
            disabled={isLoading}
            className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-sm transition-organic ${
              isSaved 
                ? 'bg-destructive/90 text-destructive-foreground' 
                : 'bg-background/90 text-foreground hover:bg-background'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={isSaved ? 'Remove from saved' : 'Save listing'}
          >
            <Icon 
              name={isSaved ? 'Heart' : 'Heart'} 
              size={18} 
              className={isSaved ? 'fill-current' : ''}
            />
          </button>
        )}

        {/* Featured Badge */}
        {listing?.is_featured && (
          <div className="absolute top-3 left-3 bg-warning text-warning-foreground px-2 py-1 rounded-full text-xs font-body font-medium">
            Featured
          </div>
        )}

        {/* Google My Business Verified Badge */}
        {listing?.google_my_business_url && (
          <div className="absolute bottom-3 left-3 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-body font-medium flex items-center space-x-1">
            <Icon name="CheckCircle" size={12} />
            <span>Verified</span>
          </div>
        )}
      </div>

      {/* Enhanced Content Section */}
      <div className="p-4">
        {/* Business Name & Logo */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {listing?.logo_url && listing?.logo_url !== getMainImage() && (
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-muted">
                <Image
                  src={listing?.logo_url}
                  alt={`${listing?.name} logo`}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="font-heading font-semibold text-foreground text-sm truncate">
                {listing?.name}
              </h3>
            </div>
          </div>
        </div>

        {/* Enhanced Description */}
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {listing?.short_description || listing?.description || 'No description available'}
        </p>

        {/* Enhanced Star Rating Display */}
        {((listing?.google_rating || listing?.average_rating) && (listing?.google_rating > 0 || listing?.average_rating > 0)) && (
          <div className="mb-3">
            <StarRating 
              rating={listing?.google_rating || listing?.average_rating} 
              reviewCount={listing?.google_review_count || listing?.total_reviews}
              size="sm"
              showReviewCount={true}
              className="text-xs"
            />
            {listing?.google_my_business_url && (
              <div className="flex items-center space-x-1 mt-1">
                <Icon name="ExternalLink" size={12} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Google Reviews</span>
              </div>
            )}
          </div>
        )}

        {/* Enhanced Location & Distance */}
        <div className="flex items-center space-x-1 mb-3">
          <Icon name="MapPin" size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="text-xs text-muted-foreground truncate">
            {formatLocation()}
          </span>
          {listing?.distance && formatDistance(listing?.distance) && (
            <>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground">
                {formatDistance(listing?.distance)}
              </span>
            </>
          )}
        </div>

        {/* Enhanced Category Badges */}
        {safeCategories?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {safeCategories?.slice(0, 2)?.map((category, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full font-body"
              >
                {category}
              </span>
            ))}
            {safeCategories?.length > 2 && (
              <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full font-body">
                +{safeCategories?.length - 2}
              </span>
            )}
          </div>
        )}

        {/* Enhanced Footer with Additional Info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 text-xs text-muted-foreground">
            {listing?.verified_at && (
              <div className="flex items-center space-x-1">
                <Icon name="Shield" size={12} className="text-primary" />
                <span>Verified</span>
              </div>
            )}
            {(showViewCount && listing?.total_views > 0) && (
              <div className="flex items-center space-x-1">
                <Icon name="Eye" size={12} />
                <span>{listing?.total_views} views</span>
              </div>
            )}
          </div>
          
          {listing?.nextAvailable && (
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {listing?.nextAvailable}
              </span>
            </div>
          )}
        </div>

        {/* Enhanced Website Link */}
        {listing?.website_url && (
          <div className="mt-3 pt-3 border-t border-border">
            <a
              href={listing?.website_url?.startsWith('http') ? listing?.website_url : `https://${listing?.website_url}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e?.stopPropagation()}
              className="flex items-center space-x-2 text-xs text-primary hover:text-primary/80 transition-gentle"
            >
              <Icon name="Globe" size={12} />
              <span>Visit Website</span>
              <Icon name="ExternalLink" size={10} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingCard;